import "./index.css";
import "./searchResults.css";
import {
  SearchResult,
  SearchResultResponse,
  VariablesToHydrate,
} from "./types/search.types";
import { ResultCardService } from "./utils/resultCard.util";
import { FormService } from "./utils/form.utils";
import { ApiService } from "./utils/api.utils";
import { getUrlParam, parseUrlParams } from "./utils/urlParams.utils";
import { docReady } from "./utils/dom.util";

const SELECTOR_LIST = ".pls2__search-results__list";
const SELECTOR_TEMPLATE = "template";
const SELECTOR_BUTTON_VIEW = ".pls2__search-results__button-view";
const SELECTOR_LOADER = ".pls2__search-results__loader";
const SELECTOR_BUTTON_LOAD_MORE = "[data-item='button-load-more']";
const SELECTOR_FORM_DETAIL = "[data-item='form-detail']";
const SELECTOR_FORM_NO_RESULTS = "[data-item='form-no-results']";
const SELECTOR_FORM_LOAD_MORE = "[data-item='form-load-more']";

class SearchResults {
  public el: HTMLDivElement;
  public listElement: HTMLDivElement;
  public templateEl: HTMLTemplateElement;
  public formDetail: FormService;
  public formNoResults: FormService;
  public formLoadMore: FormService;
  public resultCardService: ResultCardService;
  public apiService: ApiService;
  public resultsPerPage: number;
  public maxResults: number;
  public btnLoadMore: HTMLButtonElement;
  public loaderEl: HTMLDivElement;

  constructor(el: HTMLDivElement) {
    this.el = el;
    this.resultCardService = new ResultCardService();

    this.resultsPerPage = parseInt(this.el.dataset.resultsPerPage!);
    this.maxResults = parseInt(this.el.dataset.maxResults!);
    this.apiService = new ApiService({
      resultsPerPage: this.resultsPerPage,
      maxResults: this.maxResults,
    });

    this.btnLoadMore = this.el.querySelector(SELECTOR_BUTTON_LOAD_MORE)!;

    this.loaderEl = this.el.querySelector(SELECTOR_LOADER)!;

    this.listElement = this.el.querySelector(SELECTOR_LIST) as HTMLDivElement;
    this.templateEl = this.el.querySelector(
      SELECTOR_TEMPLATE
    ) as HTMLTemplateElement;

    this.formDetail = new FormService(
      this.el.querySelector(SELECTOR_FORM_DETAIL) as HTMLDivElement
    );

    this.formNoResults = new FormService(
      this.el.querySelector(SELECTOR_FORM_NO_RESULTS) as HTMLDivElement
    );

    this.formLoadMore = new FormService(
      this.el.querySelector(SELECTOR_FORM_LOAD_MORE) as HTMLDivElement
    );

    this.fetchData();
  }

  checkSearchBarForPrefill() {
    const searchBar = document.querySelector(
      ".xchange-public-leasing-search-bar"
    );

    if (!searchBar) return;

    const prefillLocation = searchBar.getAttribute("data-prefill-pickup");
    const prefillAction = searchBar.getAttribute("data-prefill-action");

    return { prefillLocation, prefillAction };
  }

  async fetchData() {
    const urlParams = parseUrlParams();

    const showDefaults = this.el.getAttribute("data-show-defaults");

    if (showDefaults) {
      const prefillData = this.checkSearchBarForPrefill();

      const isUserInitiatedSearch = getUrlParam("pickUpLocationUnlocode");

      if (prefillData && !isUserInitiatedSearch) {
        prefillData.prefillLocation &&
          urlParams.pickUpLocationUnlocode?.push(prefillData.prefillLocation);

        if (prefillData.prefillAction) {
          prefillData.prefillAction === "supply"
            ? (urlParams.direction = "Supplying")
            : (urlParams.direction = "Using");
        }
      }
    }

    if (
      urlParams.pickUpLocationUnlocode?.length === 0 &&
      urlParams.dropOffLocationUnlocode?.length === 0
    ) {
      this.setLoading(false);
      return;
    }

    this.apiService.createSearchParams(urlParams);

    try {
      this.setLoading(true);

      // this.setLoading(false);

      // this.setTotalResults(results.totalElements);

      // No Results, show no results message and return early
      // if (results.content.length === 0 && results.empty) {
      //   this.noResultsEl.classList.add("-visible");
      //   this.setLoadMore(0, 0);

      //   return;
      // }
      const data = await this.apiService.getResults();

      this.setLoading(false);

      if (data.searchResults.length === 0) {
        this.formNoResults.show();

        return;
      }

      this.appendToResults(data);
      this.setLoadMore({
        pickupLocationName: urlParams.pickUpLocationUnlocode?.[0],
        dropOffLocationName: urlParams.dropOffLocationUnlocode?.[0],
        direction: urlParams.direction,
      });
    } catch (error) {
      console.error(error);
    }
  }

  appendToResults(data: SearchResultResponse) {
    data.searchResults.forEach((searchResult) => {
      const clone = this.templateEl.content.cloneNode(true) as HTMLDivElement;
      const buttonFormView = clone.querySelector(SELECTOR_BUTTON_VIEW);

      if (buttonFormView) {
        buttonFormView.addEventListener("click", () => {
          this.formDetail.show(searchResult);
        });
      }

      this.hydrateResultCard(searchResult, clone);
      this.listElement.appendChild(clone);
    });
  }

  setLoadMore(data: Partial<SearchResult>) {
    this.btnLoadMore.addEventListener("click", () =>
      this.formLoadMore.show(data)
    );

    this.btnLoadMore.setAttribute("data-show", "true");
  }

  hydrateResultCard(result: SearchResult, template: HTMLDivElement) {
    const variablesToHydrate: VariablesToHydrate = {
      equipmentCount: {},
      equipmentType: {},
      perDiem: {
        transformFunc: this.resultCardService.getCurrencyFormat,
      },
      pickupLocationName: {
        tag: "location-pickup",
      },
      dropOffLocationName: {
        tag: "location-dropoff",
      },
      whoPays: {
        transformFunc: this.resultCardService.getPayee,
      },
      averageRating: {
        rating: true,
        transformFunc: (rating: number) => rating.toFixed(1),
      },
      freeDays: {},
      pickupCharge: {
        transformFunc: (price) => {
          return this.resultCardService.getCurrencyFormat(price, 0);
        },
      },
      // commercialTerm: {
      //   classes: { PICK_UP: "-blue", GATE_BUY: "-green" },
      //   transformFunc: this.resultCardService.getCommercialTerm,
      // },
      // imageName: {
      //   tag: "image-src",
      //   isImage: true,
      //   transformFunc: this.resultCardService.getImageUrl,
      // },
      // nonNegotiable: {
      //   transformFunc: this.resultCardService.getNegotiable,
      //   classes: { false: "-negotiable" },
      // },
      // price: {
      //   transformFunc: this.shouldBlur
      //     ? this.resultCardService.fakePrice
      //     : this.resultCardService.convertToCurrency,
      // },
      // locationDefaultName: {
      //   tag: "location",
      //   setTitle: true,
      // },
      // quantity: {
      //   transformFunc: this.resultCardService.getUnits,
      // },
      // equipmentType: {
      //   transformFunc: this.resultCardService.getEquipmentType,
      // },
      // unitCondition: {
      //   tag: "container-condition",
      //   setTitle: true,
      //   transformFunc: this.resultCardService.getUnitCondition,
      // },
      // yearOfManufactureRangeFrom: {
      //   transformFunc: this.resultCardService.getYearOfManufacture,
      //   tag: "yom-from",
      //   tagParent: "-not-specified",
      // },
      // yearOfManufactureRangeTo: {
      //   transformFunc: this.resultCardService.getYearOfManufacture,
      //   tag: "yom-to",
      //   hideElement: true,
      // },
      // companyRatingAverage: {
      //   tag: "rating",
      //   transformFunc: (rating: number) => rating.toFixed(1),
      // },
      // offerCategory: {
      //   tag: "action",
      //   transformFunc: this.resultCardService.getOfferCategory,
      // },
    };

    const variables = Object.keys(variablesToHydrate);

    for (const variable of variables) {
      const settings = variablesToHydrate[variable];
      const selector =
        settings.tag || this.resultCardService.toKebabCase(variable);

      const element = template.querySelector(
        `[data-tag-${selector}]`
      ) as HTMLElement;

      if (settings.noValue) {
        continue;
      }

      let value = result[variable];

      if (settings.tagParent) {
        element.parentElement!.classList.add(settings.tagParent);
      }

      if (settings.transformFunc) {
        value = settings.transformFunc(value);
      }

      if (settings.isImage) {
        element.setAttribute("src", value);

        continue;
      }

      if (settings.setTitle) {
        element.setAttribute("title", value);
      }

      if (settings.rating) {
        element.parentElement!.parentElement!.style.setProperty(
          "--rating",
          value.toString()
        );
      }

      element.innerText = value;
    }
  }

  setLoading(loading: boolean) {
    this.el.dataset.loading = loading.toString();
  }
}

docReady(() => {
  const el = document.querySelectorAll(
    ".xchange-public-leasing-search-results"
  ) as NodeListOf<HTMLDivElement>;

  if (el) {
    el.forEach((element) => new SearchResults(element));
  }
});
